/**
 * Display-only thousands separators for a MathLive field (bug 535512).
 *
 * MathLive's editable render keeps one span per atom — every digit is its own
 * `<span class="ML__cmr" data-atom-id="…">8</span>` — so an integer run can be regrouped by
 * marking every third-from-last digit span with a class whose `::after` draws a comma. The LaTeX
 * value is never touched: storage, evaluation, copy/paste and the caret all see the plain number.
 * (The static `convertLatexToMarkup` render coalesces digits into one span; this pass only ever
 * matches single-glyph spans, so it is inert there by construction.)
 */

import { thousandsSeparatorPositions } from '../../utils/thousandsSeparatorPositions';

export const DIGIT_GROUP_CLASS = 'mlf-comma-after';

// Injected into the <math-field> shadow root — page CSS cannot reach the rendered content.
export const DIGIT_GROUP_STYLE = `.${DIGIT_GROUP_CLASS}::after { content: ','; }`;

const DIGITS = '0123456789';

// MathLive renders the caret as its own span, either as a sibling of the digit spans or nested
// inside the span of the atom it sits on — in both places it must not break a digit run.
function isCaret(element: Element): boolean {
  return element.classList.contains('ML__caret') || element.classList.contains('ML__text-caret');
}

/** The single glyph an element draws itself (ignoring an embedded caret), or '' if it isn't one. */
function ownGlyph(element: Element): string {
  let text = '';
  for (const node of Array.from(element.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? '';
    else if (!(node instanceof Element && isCaret(node))) return '';
  }
  return text.length === 1 ? text : '';
}

function markRun(run: Element[]): void {
  for (const index of thousandsSeparatorPositions(run.length)) {
    run[index].classList.add(DIGIT_GROUP_CLASS);
  }
}

/**
 * Re-derive the comma marks for everything under `root`. Idempotent — clears previous marks first —
 * and safe to run on every render: it only toggles a class, never adds or removes nodes.
 */
export function placeDigitGroupSeparators(root: ParentNode): void {
  for (const marked of Array.from(root.querySelectorAll(`.${DIGIT_GROUP_CLASS}`))) {
    marked.classList.remove(DIGIT_GROUP_CLASS);
  }

  // Digit spans of one number are siblings inside their row container (base line, fraction half,
  // root body…), so runs are found by walking each such parent's children in order.
  const parents = new Set<Element>();
  for (const element of Array.from(root.querySelectorAll('*'))) {
    const glyph = ownGlyph(element);
    if (glyph && DIGITS.includes(glyph) && element.parentElement) parents.add(element.parentElement);
  }

  for (const parent of parents) {
    let run: Element[] = [];
    let inDecimal = false;
    const endRun = () => {
      markRun(run);
      run = [];
      inDecimal = false;
    };

    for (const child of Array.from(parent.children)) {
      if (isCaret(child)) continue;
      const glyph = ownGlyph(child);
      if (glyph && DIGITS.includes(glyph)) {
        // Digits after the decimal point stay ungrouped (1,234.5678).
        if (!inDecimal) run.push(child);
        continue;
      }
      if (glyph === '.') {
        markRun(run);
        run = [];
        inDecimal = true;
        continue;
      }
      endRun();
    }
    endRun();
  }
}

/**
 * Turn digit grouping on for an upgraded `<math-field>`: inject the comma style into its (open)
 * shadow root, decorate the current content, and keep the marks fresh across MathLive's renders.
 * Returns a detach callback, or undefined when the element exposes no rendered content.
 */
export function attachDigitGrouping(element: HTMLElement): (() => void) | undefined {
  const shadowRoot = element.shadowRoot;
  const content = shadowRoot?.querySelector('[part=content]');
  if (!shadowRoot || !content) return undefined;

  if (!shadowRoot.querySelector('style[data-mlf-digit-group]')) {
    const style = document.createElement('style');
    style.setAttribute('data-mlf-digit-group', '');
    style.textContent = DIGIT_GROUP_STYLE;
    shadowRoot.appendChild(style);
  }

  placeDigitGroupSeparators(content);
  // MathLive re-renders by replacing the content's innerHTML wholesale, which wipes the marks.
  // The pass only toggles a class — an attribute mutation — so a childList observer re-applies
  // after every render without ever observing its own work.
  const observer = new MutationObserver(() => placeDigitGroupSeparators(content));
  observer.observe(content, { childList: true, subtree: true });
  return () => observer.disconnect();
}
